import type { ChangeLogEntryDto, ChangeLogFieldChangeDto } from '../../../../../api/created/changeLog.ts';
import type { UserDto } from '../../../../../api/generated/model';
import type {
  ChangeHistoryEntryData,
  HistoryAction,
  HistoryFieldChange,
} from '../../../../../components/history/historyTypes.ts';
import { getSalutationName } from '../../../../../utils/salutation.ts';
import { resolveChangedByName } from '../../../../../utils/user.ts';

export type ReferencePersonFieldChange = HistoryFieldChange;

export type ReferencePersonHistoryEntry = ChangeHistoryEntryData & {
  id: string;
  action: HistoryAction;
  actionLabel: string;
  changedByName: string;
};

export type ReferencePersonHistoryMappingContext = {
  users: UserDto[];
};

const REFERENCE_PERSON_FIELD_LABELS: Record<string, string> = {
  salutation: 'Anrede',
  last_name: 'Name',
  lastName: 'Name',
  first_name: 'Vorname',
  firstName: 'Vorname',
  position: 'Funktion',
  function: 'Funktion',
  phone_number: 'Telefon Nr.',
  phoneNumber: 'Telefon Nr.',
  phone: 'Telefon Nr.',
  email: 'E-Mail',
  person_id: 'Person',
  personId: 'Person',
};

const ACTION_LABELS: Record<HistoryAction, string> = {
  created: 'wurde erstellt von',
  updated: 'wurde geändert von',
  deleted: 'wurde gelöscht von',
};

function mapOperationToAction(operation: string): HistoryAction {
  const normalizedOperation = operation.trim().toUpperCase();

  if (normalizedOperation.includes('CREATE') || normalizedOperation.includes('INSERT')) {
    return 'created';
  }

  if (normalizedOperation.includes('DELETE') || normalizedOperation.includes('REMOVE')) {
    return 'deleted';
  }

  return 'updated';
}

function getReferencePersonFieldLabel(fieldName: string) {
  return REFERENCE_PERSON_FIELD_LABELS[fieldName] ?? fieldName;
}

export function formatReferencePersonFieldValue(fieldName: string, value: string | null | undefined) {
  if (value == null || !value.trim()) {
    return value ?? '';
  }

  if (fieldName === 'salutation') {
    const name = getSalutationName(value);
    return name === '-' ? value : name;
  }

  return value;
}

function mapFieldChange(change: ChangeLogFieldChangeDto): HistoryFieldChange {
  return {
    fieldLabel: getReferencePersonFieldLabel(change.fieldName),
    oldValue: formatReferencePersonFieldValue(change.fieldName, change.oldValue),
    newValue: formatReferencePersonFieldValue(change.fieldName, change.newValue),
  };
}

export function mapChangeLogEntryToReferencePersonHistoryEntry(
  entry: ChangeLogEntryDto,
  context: ReferencePersonHistoryMappingContext,
): ReferencePersonHistoryEntry {
  const action = mapOperationToAction(entry.operation);

  return {
    id: entry.changeSetId,
    date: entry.changedAt,
    action,
    actionLabel: ACTION_LABELS[action],
    changedByName: resolveChangedByName(entry.changedBy, context.users),
    changes: entry.changes.map((change) => mapFieldChange(change)),
  };
}

export function mapChangeLogEntriesToReferencePersonHistory(
  entries: ChangeLogEntryDto[],
  context: ReferencePersonHistoryMappingContext,
): ReferencePersonHistoryEntry[] {
  return entries.map((entry) => mapChangeLogEntryToReferencePersonHistoryEntry(entry, context));
}
